/**
 * Web Push helpers.
 *  - `isPushSupported()` — quick feature check.
 *  - `getPushPermissionState()` — 'granted' | 'denied' | 'default' | 'unsupported'.
 *  - `subscribePush()` — asks for permission (if needed), subscribes via SW, posts to backend.
 *  - `unsubscribePush()` — reverses the above.
 *  - `getExistingSubscription()` — current PushSubscription or null.
 */
import api from "@/lib/api";

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || "";

export function isPushSupported() {
  return typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window
    && !!VAPID_PUBLIC_KEY;
}

export function getPushPermissionState() {
  if (!isPushSupported()) return "unsupported";
  return Notification.permission; // 'granted' | 'denied' | 'default'
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function getRegistration() {
  const reg = await navigator.serviceWorker.getRegistration();
  if (reg) return reg;
  // Fallback: register on demand (index.js already does this on load).
  return navigator.serviceWorker.register("/service-worker.js");
}

export async function getExistingSubscription() {
  if (!isPushSupported()) return null;
  const reg = await getRegistration();
  return reg.pushManager.getSubscription();
}

export async function subscribePush() {
  if (!isPushSupported()) throw new Error("Push notifications are not supported in this browser.");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Notification permission was not granted.");
  const reg = await getRegistration();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
  }
  const payload = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: payload.endpoint,
    keys: payload.keys,
  });
  return sub;
}

export async function unsubscribePush() {
  if (!isPushSupported()) return false;
  const reg = await getRegistration();
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return true;
  const payload = sub.toJSON();
  try {
    await api.post("/push/unsubscribe", { endpoint: payload.endpoint, keys: payload.keys });
  } catch (e) {
    // best-effort — still cancel the local subscription
  }
  await sub.unsubscribe();
  return true;
}
